import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { shouldFetch } from 'app/utils/utils';
import { _InvitesListLinks, Invite } from 'types/invite.types';
import { _NotificationListLinks, Notification } from 'types/notification.types';
import { LoadingState } from 'types/enums';
import {
  fetchInvitesEffect,
  fetchNotificationsEffect,
  fetchUnseenInviteCountEffect,
  fetchUnseenNotificationCountEffect,
  markNotificationAsReadEffect,
  respondToInviteEffect,
} from './effects';

export interface NotificationsState {
  notifications: Notification[];
  invites: Invite[];
  notificationsLoadingState: LoadingState;
  _notificationsListLinks?: _NotificationListLinks;
  _invitesListLinks?: _InvitesListLinks;
  unseenNotificationCount: number;
  invitesLoadingState: LoadingState;
  invitesMoreLoadingState: LoadingState;
  unseenInviteCount: number;
  totalCount: number;
  newNotificationsCount: number;
  newInvitesCount: number;
  unreadChatMessagesCount: number;
}

export const notificationsInitialState: NotificationsState = {
  notifications: [],
  invites: [],
  notificationsLoadingState: LoadingState.PRISTINE,
  unseenNotificationCount: 0,
  unseenInviteCount: 0,
  invitesLoadingState: LoadingState.PRISTINE,
  invitesMoreLoadingState: LoadingState.PRISTINE,
  totalCount: 0,
  newNotificationsCount: 0,
  newInvitesCount: 0,
  unreadChatMessagesCount: 0,
};

const slice = createSlice({
  name: 'notifications',
  initialState: notificationsInitialState,
  reducers: {
    resetLoadingStateAction(state) {
      state.notificationsLoadingState = LoadingState.PRISTINE;
      state.invitesLoadingState = LoadingState.PRISTINE;
    },
    markNotificationAsOldAction(state, { payload }: PayloadAction<string>) {
      const notification = state.notifications.find(
        (notif) => notif.guid === payload
      );
      if (notification) {
        notification.new = false;
      }
    },
    removeInviteAction(state, { payload }: PayloadAction<string>) {
      state.invites = state.invites.filter((invite) => invite.guid !== payload);
    },
    updateNewNotificationsCountsAction(state, { payload }) {
      state.newNotificationsCount = payload.newNotificationsCount;
      state.newInvitesCount = payload.newInvitesCount;
      state.totalCount = payload.totalCount;
      state.unreadChatMessagesCount = payload.unreadChatMessagesCount;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsEffect.pending, (state) => {
        if (shouldFetch(state.notificationsLoadingState)) {
          state.notificationsLoadingState = LoadingState.FETCHING;
        }
      })
      .addCase(
        fetchNotificationsEffect.fulfilled,
        (state, { meta, payload: { items, _links } }) => {
          if (meta.arg?.next) {
            state.notifications.push(...items);
          } else if (
            state.notificationsLoadingState === LoadingState.FETCHING
          ) {
            state.notifications = items;
          } else {
            state.notifications = [
              ...items.filter(
                (newNotification) =>
                  !state.notifications.some(
                    (oldNotification) =>
                      newNotification.guid === oldNotification.guid
                  ),
                state.notifications
              ),
              ...state.notifications,
            ];
          }
          if (
            meta.arg?.next ||
            state.notificationsLoadingState === LoadingState.FETCHING
          ) {
            state._notificationsListLinks = _links;
          }
          state.notificationsLoadingState = LoadingState.FETCHED;
        }
      )
      .addCase(fetchNotificationsEffect.rejected, (state) => {
        state.notificationsLoadingState = LoadingState.ERROR;
      })
      .addCase(
        fetchUnseenNotificationCountEffect.fulfilled,
        (state, { payload }) => {
          state.newNotificationsCount = payload.newNotificationsCount;
          state.newInvitesCount = payload.newInvitesCount;
          state.totalCount = payload.totalCount;
          state.unreadChatMessagesCount = payload.unreadChatMessagesCount;
        }
      )
      .addCase(fetchUnseenInviteCountEffect.fulfilled, (state, { payload }) => {
        state.unseenInviteCount = payload;
      })
      .addCase(fetchInvitesEffect.pending, (state, { meta }) => {
        if (shouldFetch(state.invitesLoadingState)) {
          state.invitesLoadingState = LoadingState.FETCHING;
        }
        if (meta.arg?.next) {
          state.invitesMoreLoadingState = LoadingState.FETCHING;
        }
      })
      .addCase(
        fetchInvitesEffect.fulfilled,
        (state, { payload: { _links, items }, meta }) => {
          if (meta.arg?.next) {
            state.invites.push(...items);
          } else if (state.invitesLoadingState === LoadingState.FETCHING) {
            state.invites = items;
          } else {
            state.invites = [
              ...items.filter(
                (newInvite) =>
                  !state.invites.some(
                    (oldInvite) => newInvite.guid === oldInvite.guid
                  ),
                state.invites
              ),
              ...state.invites,
            ];
          }
          if (
            meta.arg?.next ||
            state.invitesLoadingState === LoadingState.FETCHING
          ) {
            state._invitesListLinks = _links;
          }
          state.invitesLoadingState = LoadingState.FETCHED;
          state.invitesMoreLoadingState = LoadingState.FETCHED;
        }
      )
      .addCase(fetchInvitesEffect.rejected, (state) => {
        state.invitesLoadingState = LoadingState.ERROR;
        state.invitesMoreLoadingState = LoadingState.ERROR;
      })
      .addCase(respondToInviteEffect.fulfilled, (state, { meta }) => {
        state.invites = state.invites.filter(
          (invite) => invite.guid !== meta.arg.guid
        );
      })
      .addCase(markNotificationAsReadEffect.fulfilled, (state, { meta }) => {
        const notification = state.notifications.find(
          (notif) => notif.guid === meta.arg.guid
        );
        if (notification) {
          notification.read = true;
        }
      });
  },
});

export const {
  resetLoadingStateAction,
  markNotificationAsOldAction,
  removeInviteAction,
  updateNewNotificationsCountsAction,
} = slice.actions;

export const notificationsReducer = slice.reducer;
